import React, { useEffect, useState } from "react";
import {
  Box,
  InputLabel,
  TextField,
  FormControl,
  FormHelperText,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Switch,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { TimeField } from "@mui/x-date-pickers/TimeField";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Controller, useFormContext } from "react-hook-form";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import EngineeringOutlinedIcon from "@mui/icons-material/EngineeringOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import useTranslation from "../../../shared/hooks/useTranslation";
import DropdownIcon from "../../../shared/assets/icons/DropdownIcon";
import IconDatePicker from "./shared/IconDatePicker";
import {
  ServiceTypeCode,
  PriorityTypeCode,
  PaymentTermsCode,
  BudgetTypeCode,
  ServiceRequestFormData,
} from "../models/models";
import { twoDecimalsRegex } from "../../../shared/validators";
import { toBoolean } from "../../../shared/utils/booleanUtils";
import { useCommonFormatters } from "../hooks/useCommonFormatters";
import { dt } from "../../../shared/themes/redesignTokens";

// ═══════════════════════════════════════════════════
//  SERVICE TYPE CARD CONFIG
// ═══════════════════════════════════════════════════

interface ServiceTypeOption {
  code: ServiceTypeCode;
  icon: React.ReactNode;
  labelKey: string;
  descKey: string;
}

const SERVICE_TYPE_OPTIONS: ServiceTypeOption[] = [
  {
    code: ServiceTypeCode.onSiteRepair,
    icon: <BuildOutlinedIcon />,
    labelKey: "myMachinery:serviceTypes.onSiteRepair",
    descKey: "servicing:serviceTypeCards.onSiteRepairDesc",
  },
  {
    code: ServiceTypeCode.maintenance,
    icon: <EngineeringOutlinedIcon />,
    labelKey: "myMachinery:serviceTypes.maintenance",
    descKey: "servicing:serviceTypeCards.maintenanceDesc",
  },
  {
    code: ServiceTypeCode.partsReplacement,
    icon: <SwapHorizOutlinedIcon />,
    labelKey: "myMachinery:serviceTypes.partsReplacement",
    descKey: "servicing:serviceTypeCards.partsReplacementDesc",
  },
];

// ═══════════════════════════════════════════════════
//  URGENCY CARD CONFIG
// ═══════════════════════════════════════════════════

interface UrgencyOption {
  code: PriorityTypeCode;
  icon: React.ReactNode;
  color: string;
  colorSoft: string;
  colorGlow: string;
}

const URGENCY_OPTIONS: UrgencyOption[] = [
  {
    code: PriorityTypeCode.urgent,
    icon: <ErrorOutlineOutlinedIcon />,
    color: "#E74C3C",
    colorSoft: "rgba(231, 76, 60, 0.06)",
    colorGlow: "rgba(231, 76, 60, 0.12)",
  },
  {
    code: PriorityTypeCode.high,
    icon: <WarningAmberOutlinedIcon />,
    color: "#F39C12",
    colorSoft: "rgba(243, 156, 18, 0.06)",
    colorGlow: "rgba(243, 156, 18, 0.12)",
  },
  {
    code: PriorityTypeCode.low,
    icon: <CheckCircleOutlineOutlinedIcon />,
    color: "#27AE60",
    colorSoft: "rgba(39, 174, 96, 0.06)",
    colorGlow: "rgba(39, 174, 96, 0.12)",
  },
];

// ═══════════════════════════════════════════════════
//  COMPONENT
// ═══════════════════════════════════════════════════

const StepSpecification = () => {
  const { translate } = useTranslation(["myMachinery", "servicing", "common"]);
  const isPhone = useMediaQuery("(max-width:768px)");

  const [isStartDateCalendarOpen, setIsStartDateCalendarOpen] = useState(false);
  const [isEndDateCalendarOpen, setIsEndDateCalendarOpen] = useState(false);

  const { formatPriorityTypeName, formatPriorityTypeDescription } =
    useCommonFormatters();

  const {
    register,
    watch,
    control,
    trigger,
    setValue,
    formState: { errors },
  } = useFormContext<ServiceRequestFormData>();

  const serviceTypeCode = watch("serviceTypeCode");
  const priorityTypeCode = watch("priorityTypeCode");
  const flexibleDates = watch("flexibleDates");
  const worksiteAccessEnabled = watch("worksiteAccessEnabled");
  const budgetEnabled = watch("budgetEnabled");
  const budgetTypeCode = watch("budgetTypeCode");
  const paymentTermsEnabled = watch("paymentTermsEnabled");
  const paymentTermsCode = watch("paymentTermsCode");

  useEffect(() => {
    if (paymentTermsEnabled && !paymentTermsCode) {
      setValue("paymentTermsCode", PaymentTermsCode.net30Days);
    }
  }, [paymentTermsEnabled]);

  /* ── shared section card style ── */
  const sectionSx = {
    backgroundColor: dt.cardBg,
    borderRadius: dt.radius,
    border: `1px solid ${dt.cardBorder}`,
    boxShadow: dt.cardShadow,
    p: { xs: 2.5, md: 4 },
    display: "flex",
    flexDirection: "column" as const,
    gap: 3,
  };

  const headingSx = {
    fontFamily: dt.font,
    fontSize: { xs: "16px", md: "18px" },
    fontWeight: 700,
    color: dt.textPrimary,
  };

  const labelSx = {
    fontFamily: dt.font,
    fontSize: "13px",
    fontWeight: 600,
    color: dt.textPrimary,
    letterSpacing: "0.1px",
    mb: 0.5,
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        animation: "fadeUp 0.5s cubic-bezier(0.4,0,0.2,1) both",
      }}
    >
      {/* ═══════════════════════════════════════
          SERVICE SPECIFICATION
          ═══════════════════════════════════════ */}
      <Box sx={sectionSx}>
        <Box sx={headingSx}>
          {translate("myMachinery:serviceSpecification")}
        </Box>

        {/* ── Service Type Radio Cards ── */}
        <Box>
          <InputLabel required sx={labelSx}>
            {translate("myMachinery:serviceType")}
          </InputLabel>
          <Controller
            control={control}
            name="serviceTypeCode"
            rules={{ required: translate("common:forms.required") }}
            render={({ field }) => (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isPhone ? "column" : "row",
                  gap: 1.5,
                }}
              >
                {SERVICE_TYPE_OPTIONS.map((option, index) => {
                  const isSelected = Number(field.value) === option.code;
                  return (
                    <Box
                      key={option.code}
                      onClick={() => {
                        field.onChange(option.code);
                        setTimeout(() => trigger("serviceTypeCode"));
                      }}
                      sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                        p: 2,
                        borderRadius: dt.radiusSm,
                        border: `2px solid ${isSelected ? dt.blue : dt.cardBorder}`,
                        backgroundColor: isSelected ? dt.blueSoft : dt.cardBg,
                        boxShadow: isSelected
                          ? `0 0 0 3px ${dt.blueGlow}`
                          : "none",
                        cursor: "pointer",
                        transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
                        animation: `fadeUp 0.4s cubic-bezier(0.4,0,0.2,1) ${index * 0.05}s both`,
                        "&:hover": {
                          borderColor: isSelected ? dt.blue : "#D1D5DB",
                          boxShadow: isSelected
                            ? `0 0 0 3px ${dt.blueGlow}`
                            : dt.cardShadowHover,
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      {/* Icon circle */}
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: isSelected
                            ? `linear-gradient(135deg, ${dt.blue} 0%, #2E86C1 100%)`
                            : dt.inputBg,
                          transition: "all 0.25s ease",
                          "& .MuiSvgIcon-root": {
                            fontSize: 20,
                            color: isSelected ? "#fff" : dt.textMuted,
                            transition: "color 0.25s ease",
                          },
                        }}
                      >
                        {option.icon}
                      </Box>

                      <Typography
                        sx={{
                          fontFamily: dt.font,
                          fontSize: "14px",
                          fontWeight: 600,
                          color: isSelected ? dt.blue : dt.textPrimary,
                          textAlign: "center",
                        }}
                      >
                        {translate(option.labelKey)}
                      </Typography>

                      <Typography
                        sx={{
                          fontFamily: dt.font,
                          fontSize: "12px",
                          color: dt.textSecondary,
                          textAlign: "center",
                          lineHeight: 1.4,
                        }}
                      >
                        {translate(option.descKey) || ""}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          />
          <FormHelperText error sx={{ mt: 0.5 }}>
            {errors.serviceTypeCode?.message}
          </FormHelperText>
        </Box>

        {/* ── Urgency Radio Cards ── */}
        <Box>
          <InputLabel required sx={labelSx}>
            {translate("myMachinery:urgency")}
          </InputLabel>
          <Controller
            control={control}
            name="priorityTypeCode"
            rules={{ required: translate("common:forms.required") }}
            render={({ field }) => (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isPhone ? "column" : "row",
                  gap: 1.5,
                }}
              >
                {URGENCY_OPTIONS.map((option, index) => {
                  const isSelected = Number(field.value) === option.code;
                  return (
                    <Box
                      key={option.code}
                      onClick={() => {
                        field.onChange(option.code);
                        setTimeout(() => trigger("priorityTypeCode"));
                      }}
                      sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        p: 2,
                        borderRadius: dt.radiusSm,
                        border: `2px solid ${isSelected ? option.color : dt.cardBorder}`,
                        borderLeft: `4px solid ${isSelected ? option.color : dt.cardBorder}`,
                        backgroundColor: isSelected ? option.colorSoft : dt.cardBg,
                        boxShadow: isSelected
                          ? `0 0 0 3px ${option.colorGlow}`
                          : "none",
                        cursor: "pointer",
                        transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
                        animation: `fadeUp 0.4s cubic-bezier(0.4,0,0.2,1) ${index * 0.05}s both`,
                        "&:hover": {
                          borderColor: isSelected ? option.color : "#D1D5DB",
                          boxShadow: isSelected
                            ? `0 0 0 3px ${option.colorGlow}`
                            : dt.cardShadowHover,
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      {/* Color indicator */}
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          background: isSelected
                            ? option.color
                            : dt.inputBg,
                          transition: "all 0.25s ease",
                          flexShrink: 0,
                          "& .MuiSvgIcon-root": {
                            fontSize: 20,
                            color: isSelected ? "#fff" : dt.textMuted,
                          },
                        }}
                      >
                        {option.icon}
                      </Box>

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontFamily: dt.font,
                            fontSize: "14px",
                            fontWeight: 600,
                            color: isSelected ? option.color : dt.textPrimary,
                          }}
                        >
                          {formatPriorityTypeName(option.code)}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: dt.font,
                            fontSize: "12px",
                            color: dt.textSecondary,
                            lineHeight: 1.4,
                          }}
                        >
                          {formatPriorityTypeDescription(option.code)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          />
          <FormHelperText error sx={{ mt: 0.5 }}>
            {errors.priorityTypeCode?.message}
          </FormHelperText>
        </Box>

        {/* ── Flexible dates radio group ── */}
        <Controller
          control={control}
          name="flexibleDates"
          render={({ field }) => (
            <RadioGroup
              {...field}
              value={flexibleDates || false}
              sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
            >
              <FormControlLabel
                label={
                  <Typography sx={{ fontFamily: dt.font, fontSize: "14px", fontWeight: 500 }}>
                    {translate("servicing:noPreferredDates")}
                  </Typography>
                }
                value={true}
                control={
                  <Radio
                    sx={{
                      color: dt.textMuted,
                      "&.Mui-checked": { color: dt.blue },
                    }}
                  />
                }
                sx={{ marginRight: 5 }}
              />
              <FormControlLabel
                label={
                  <Typography sx={{ fontFamily: dt.font, fontSize: "14px", fontWeight: 500 }}>
                    {translate("servicing:chooseServiceWindow")}
                  </Typography>
                }
                value={false}
                control={
                  <Radio
                    sx={{
                      color: dt.textMuted,
                      "&.Mui-checked": { color: dt.blue },
                    }}
                  />
                }
                sx={{ marginRight: 5 }}
              />
            </RadioGroup>
          )}
        />

        {/* ── Service date range ── */}
        <Box
          sx={{
            opacity: toBoolean(flexibleDates as any) !== false ? 0.5 : 1,
            transition: "opacity 0.3s ease",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isPhone ? "1fr" : "1fr 1fr",
              gap: 2,
            }}
          >
            <Box>
              <InputLabel sx={labelSx}>
                {translate("myMachinery:serviceStartDate")}
              </InputLabel>
              <Controller
                control={control}
                name="serviceStartDate"
                render={({ field }) => (
                  <IconDatePicker
                    field={field}
                    name="serviceStartDate"
                    disabled={toBoolean(flexibleDates as any) !== false}
                    isOpen={isStartDateCalendarOpen}
                    onOpen={() => setIsStartDateCalendarOpen(true)}
                    onClose={() => setIsStartDateCalendarOpen(false)}
                  />
                )}
              />
            </Box>

            <Box>
              <InputLabel sx={labelSx}>
                {translate("myMachinery:serviceEndDate")}
              </InputLabel>
              <Controller
                control={control}
                name="serviceEndDate"
                render={({ field }) => (
                  <IconDatePicker
                    field={field}
                    name="serviceEndDate"
                    disabled={toBoolean(flexibleDates as any) !== false}
                    isOpen={isEndDateCalendarOpen}
                    onOpen={() => setIsEndDateCalendarOpen(true)}
                    onClose={() => setIsEndDateCalendarOpen(false)}
                  />
                )}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 0.5 }}>
            <FormHelperText error>
              {errors.serviceStartDate?.message ||
                errors.serviceEndDate?.message}
            </FormHelperText>
          </Box>
        </Box>
      </Box>

      {/* ═══════════════════════════════════════
          WORKSITE ACCESS
          ═══════════════════════════════════════ */}
      <Box sx={sectionSx}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={headingSx}>{translate("servicing:worksiteAccess")}</Box>
          <Switch
            className="styled"
            disableRipple
            {...register("worksiteAccessEnabled")}
            checked={worksiteAccessEnabled || false}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": {
                color: dt.blue,
              },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: dt.blue,
              },
            }}
          />
        </Box>

        {/* Expand content with smooth transition */}
        <Box
          sx={{
            maxHeight: worksiteAccessEnabled ? "500px" : "0px",
            overflow: "hidden",
            opacity: worksiteAccessEnabled ? 1 : 0,
            transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          {worksiteAccessEnabled && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "14px",
                    fontWeight: 500,
                    color: dt.textPrimary,
                  }}
                >
                  {translate("servicing:normalWorkingHours")}
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <FormControl sx={{ width: "100px" }}>
                    <Controller
                      control={control}
                      name="workingHoursFrom"
                      render={({ field }) => (
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                          <TimeField {...field} format="HH:mm" />
                        </LocalizationProvider>
                      )}
                    />
                  </FormControl>

                  <Box
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "14px",
                      fontWeight: 500,
                      color: dt.textSecondary,
                    }}
                  >
                    {translate("servicing:workingHoursTo")}
                  </Box>

                  <FormControl sx={{ width: "100px" }}>
                    <Controller
                      control={control}
                      name="workingHoursTo"
                      render={({ field }) => (
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                          <TimeField {...field} format="HH:mm" />
                        </LocalizationProvider>
                      )}
                    />
                  </FormControl>
                </Box>
              </Box>

              <Controller
                control={control}
                name="weekendWorkPossible"
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        {...field}
                        checked={field.value || false}
                        onChange={(e) => field.onChange(e.target.checked)}
                        sx={{ "&.Mui-checked": { color: dt.blue } }}
                      />
                    }
                    label={
                      <Typography sx={{ fontFamily: dt.font, fontSize: "14px" }}>
                        {translate("servicing:weekendWorkPossible")}
                      </Typography>
                    }
                  />
                )}
              />

              <Controller
                control={control}
                name="afterHourAccessAvailable"
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        {...field}
                        checked={field.value || false}
                        onChange={(e) => field.onChange(e.target.checked)}
                        sx={{ "&.Mui-checked": { color: dt.blue } }}
                      />
                    }
                    label={
                      <Typography sx={{ fontFamily: dt.font, fontSize: "14px" }}>
                        {translate("servicing:afterHourAccessAvailable")}
                      </Typography>
                    }
                  />
                )}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* ═══════════════════════════════════════
          BUDGET
          ═══════════════════════════════════════ */}
      <Box sx={sectionSx}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={headingSx}>{translate("servicing:budget")}</Box>
          <Switch
            className="styled"
            disableRipple
            {...register("budgetEnabled")}
            checked={budgetEnabled || false}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: dt.blue },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: dt.blue,
              },
            }}
          />
        </Box>

        <Box
          sx={{
            maxHeight: budgetEnabled ? "500px" : "0px",
            overflow: "hidden",
            opacity: budgetEnabled ? 1 : 0,
            transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          {budgetEnabled && (
            <RadioGroup
              value={budgetTypeCode || ""}
              sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 1 }}
            >
              <FormControlLabel
                label={
                  <Typography sx={{ fontFamily: dt.font, fontSize: "14px" }}>
                    {translate("servicing:budgetTypeCodes.requestQuote")}
                  </Typography>
                }
                value={BudgetTypeCode.requestQuote}
                control={
                  <Radio
                    sx={{ color: dt.textMuted, "&.Mui-checked": { color: dt.blue } }}
                    {...register("budgetTypeCode")}
                  />
                }
                sx={{ marginRight: 5 }}
              />

              <Box sx={{ display: "flex", gap: 1, flexWrap: isPhone ? "wrap" : "nowrap" }}>
                <FormControlLabel
                  sx={{ flex: 1, marginRight: 5 }}
                  label={
                    <Typography sx={{ fontFamily: dt.font, fontSize: "14px" }}>
                      {translate("servicing:budgetTypeCodes.fixed")}
                    </Typography>
                  }
                  value={BudgetTypeCode.fixed}
                  control={
                    <Radio
                      sx={{ color: dt.textMuted, "&.Mui-checked": { color: dt.blue } }}
                      {...register("budgetTypeCode")}
                    />
                  }
                />
                <TextField
                  sx={{
                    flex: 1,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: dt.radiusSm,
                    },
                  }}
                  type="number"
                  placeholder={translate("servicing:fixedBudgetPlaceholder")}
                  disabled={Number(budgetTypeCode) !== BudgetTypeCode.fixed}
                  {...register("fixedBudget", {
                    pattern: {
                      value: twoDecimalsRegex,
                      message: translate("common:forms.invalidValue"),
                    },
                  })}
                  error={!!errors.fixedBudget}
                  helperText={errors.fixedBudget?.message}
                />
              </Box>

              <Box sx={{ display: "flex", gap: 1, flexWrap: isPhone ? "wrap" : "nowrap" }}>
                <FormControlLabel
                  sx={{ flex: 1, marginRight: 5 }}
                  label={
                    <Typography sx={{ fontFamily: dt.font, fontSize: "14px" }}>
                      {translate("servicing:budgetTypeCodes.range")}
                    </Typography>
                  }
                  value={BudgetTypeCode.range}
                  control={
                    <Radio
                      sx={{ color: dt.textMuted, "&.Mui-checked": { color: dt.blue } }}
                      {...register("budgetTypeCode")}
                    />
                  }
                />
                <Box sx={{ flex: 1, display: "flex", gap: 1 }}>
                  <TextField
                    fullWidth
                    type="number"
                    placeholder={translate("servicing:budgetRangeMinPlaceholder")}
                    disabled={Number(budgetTypeCode) !== BudgetTypeCode.range}
                    {...register("budgetRangeFrom", {
                      pattern: {
                        value: twoDecimalsRegex,
                        message: translate("common:forms.invalidValue"),
                      },
                    })}
                    error={!!errors.budgetRangeFrom}
                    helperText={errors.budgetRangeFrom?.message}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: dt.radiusSm },
                    }}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    placeholder={translate("servicing:budgetRangeMaxPlaceholder")}
                    disabled={Number(budgetTypeCode) !== BudgetTypeCode.range}
                    {...register("budgetRangeTo", {
                      pattern: {
                        value: twoDecimalsRegex,
                        message: translate("common:forms.invalidValue"),
                      },
                    })}
                    error={!!errors.budgetRangeTo}
                    helperText={errors.budgetRangeTo?.message}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: dt.radiusSm },
                    }}
                  />
                </Box>
              </Box>
            </RadioGroup>
          )}
        </Box>
      </Box>

      {/* ═══════════════════════════════════════
          PAYMENT TERMS
          ═══════════════════════════════════════ */}
      <Box sx={sectionSx}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={headingSx}>{translate("servicing:paymentTerms")}</Box>
          <Switch
            className="styled"
            disableRipple
            {...register("paymentTermsEnabled")}
            checked={paymentTermsEnabled || false}
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: dt.blue },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                backgroundColor: dt.blue,
              },
            }}
          />
        </Box>

        <Box
          sx={{
            maxHeight: paymentTermsEnabled ? "200px" : "0px",
            overflow: "hidden",
            opacity: paymentTermsEnabled ? 1 : 0,
            transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          {paymentTermsEnabled && (
            <Box sx={{ pt: 1 }}>
              <InputLabel required sx={labelSx}>
                {translate("servicing:standardPaymentTerms")}
              </InputLabel>
              <FormControl fullWidth>
                <Controller
                  control={control}
                  name="paymentTermsCode"
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value || ""}
                      IconComponent={DropdownIcon}
                      sx={{
                        borderRadius: dt.radiusSm,
                        "& .MuiSvgIcon-root": {
                          fontSize: "16px",
                          marginRight: "16px",
                        },
                      }}
                      onClose={() =>
                        setTimeout(() => trigger("paymentTermsCode"))
                      }
                      error={!!errors.paymentTermsCode}
                    >
                      <MenuItem value={PaymentTermsCode.net30Days}>
                        {translate("servicing:paymentTermsCodes.net30Days")}
                      </MenuItem>
                      <MenuItem value={PaymentTermsCode.net60Days}>
                        {translate("servicing:paymentTermsCodes.net60Days")}
                      </MenuItem>
                      <MenuItem value={PaymentTermsCode.net90Days}>
                        {translate("servicing:paymentTermsCodes.net90Days")}
                      </MenuItem>
                      <MenuItem value={PaymentTermsCode.dueOnReceipt}>
                        {translate("servicing:paymentTermsCodes.dueOnReceipt")}
                      </MenuItem>
                      <MenuItem value={PaymentTermsCode.upfront50Completion}>
                        {translate(
                          "servicing:paymentTermsCodes.upfront50Completion"
                        )}
                      </MenuItem>
                    </Select>
                  )}
                />
                <FormHelperText error>
                  {errors.paymentTermsCode?.message}
                </FormHelperText>
              </FormControl>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default StepSpecification;
